import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Tabs, Row, Col, Table, Tag, Badge, Button, Modal,
  Select, Input, DatePicker, notification, Spin, Empty, Space, Tooltip,
} from 'antd';
import {
  ReloadOutlined, EditOutlined, UserOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import * as svc from './staffTrackingService';
import './StaffTracking.css';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_COLOR_LIVE = { active: '#52c41a', on_break: '#faad14', idle: '#fa8c16', clocked_out: '#ff4d4f', absent: '#d9d9d9' };
const STATUS_LABEL      = { active: 'Active', on_break: 'On Break', idle: 'Idle', clocked_out: 'Out', absent: 'Absent' };
const MATRIX_LABEL      = { present: 'P', absent: 'A', half_day: 'H', leave: 'L', holiday: 'Ho', undefined: '-' };
const MATRIX_CSS        = { present: 'P', absent: 'A', half_day: 'H', leave: 'L', holiday: 'Ho' };

const fmt = (t) => {
  if (!t) return '-';
  const s = t.toString();
  const m = s.match(/[T ](\d{2}:\d{2})/);
  return m ? m[1] : s.slice(0, 5);
};
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function AdminTrackingHub() {
  // Live board
  const [liveData, setLiveData]   = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveTick, setLiveTick]   = useState(0);

  // Daily attendance
  const [attDate, setAttDate]     = useState(today());
  const [attData, setAttData]     = useState([]);
  const [attLoading, setAttLoading] = useState(false);

  // Override modal
  const [overrideModal, setOverrideModal] = useState(false);
  const [overrideRow, setOverrideRow]     = useState(null);
  const [overStatus, setOverStatus]       = useState('');
  const [overLeave, setOverLeave]         = useState('');
  const [overNote, setOverNote]           = useState('');
  const [overSaving, setOverSaving]       = useState(false);

  // Monthly report
  const [repYear, setRepYear]   = useState(new Date().getFullYear());
  const [repMonth, setRepMonth] = useState(new Date().getMonth() + 1);
  const [repData, setRepData]   = useState(null);
  const [repLoading, setRepLoading] = useState(false);

  // Staff drill-down
  const [staffList, setStaffList] = useState([]);
  const [drillStaff, setDrillStaff] = useState(null);
  const [drillDate, setDrillDate]   = useState(today());
  const [drillData, setDrillData]   = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  // ── Live board ──────────────────────────────────────────────
  const loadLive = useCallback(async (silent = false) => {
    if (!silent) setLiveLoading(true);
    try {
      const r = await svc.getTeamDashboard(today());
      setLiveData(r.data);
    } catch { }
    finally { setLiveLoading(false); }
  }, []);

  useEffect(() => {
    loadLive();
    const iv = setInterval(() => loadLive(true), 120000);
    return () => clearInterval(iv);
  }, [loadLive]);

  // ── Daily attendance ────────────────────────────────────────
  const loadAttendance = useCallback(async (date) => {
    setAttLoading(true);
    try {
      const r = await svc.getDailyAttendance(date);
      setAttData(r.data || []);
    } catch { setAttData([]); }
    finally { setAttLoading(false); }
  }, []);

  // ── Monthly report ──────────────────────────────────────────
  const loadReport = async (y, m) => {
    setRepLoading(true);
    try {
      const r = await svc.getMonthlyReport(y, m);
      setRepData(r);
    } catch { setRepData(null); }
    finally { setRepLoading(false); }
  };

  // ── Staff drill-down ────────────────────────────────────────
  const loadDrill = async (sid, date) => {
    setDrillLoading(true);
    try {
      const r = await svc.getTimeline(sid, date);
      setDrillData(r.data?.timeline || []);
    } catch { setDrillData([]); }
    finally { setDrillLoading(false); }
  };

  const onTabChange = (key) => {
    if (key === 'daily')   loadAttendance(attDate); // always reload with current selected date
    if (key === 'report'  && !repData)  loadReport(repYear, repMonth);
  };

  // ── Override modal ──────────────────────────────────────────
  const openOverride = (row) => {
    setOverrideRow(row);
    setOverStatus(row.status);
    setOverLeave(row.leave_type || '');
    setOverNote(row.override_notes || '');
    setOverrideModal(true);
  };

  const saveOverride = async () => {
    setOverSaving(true);
    try {
      await svc.manualOverride({
        staff_id:  overrideRow.staff_id,
        date:      attDate,
        status:    overStatus,
        leave_type: overLeave || undefined,
        notes:     overNote,
      });
      notification.success({ message: 'Attendance updated' });
      setOverrideModal(false);
      loadAttendance(attDate);
    } catch (e) {
      notification.error({ message: e.message || 'Failed to update' });
    } finally { setOverSaving(false); }
  };

  // ── Attendance table columns ────────────────────────────────
  const attColumns = [
    { title: 'Staff', dataIndex: 'staff_name', key: 'name', render: n => <><UserOutlined style={{ marginRight: 6 }} />{n}</> },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: s => <Tag color={s === 'present' ? 'green' : s === 'absent' ? 'red' : s === 'half_day' ? 'orange' : 'blue'}>
        {s?.replace('_', ' ').toUpperCase()}
      </Tag>,
    },
    { title: 'Check-In',  dataIndex: 'check_in_time',  key: 'ci', render: fmt },
    { title: 'Check-Out', dataIndex: 'check_out_time', key: 'co', render: fmt },
    { title: 'Hours',     dataIndex: 'total_hours',    key: 'h',  render: h => h > 0 ? `${h}h` : '-' },
    {
      title: 'Override', key: 'override',
      render: (_, row) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openOverride(row)}>Edit</Button>
      ),
    },
  ];

  // ── Live board render ───────────────────────────────────────
  const renderLiveBoard = () => {
    if (liveLoading) return <Spin />;
    const allStaff = liveData?.active_staff || [];
    if (allStaff.length === 0) return <Empty description="No data available" />;
    return (
      <Row gutter={[12, 12]}>
        {allStaff.map(s => {
          const st = s.current_status || 'absent';
          return (
            <Col xs={24} sm={12} md={8} lg={6} key={s.staff_id || s.id}>
              <div className={`st-live-card ${st}`}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {s.firstName ? `${s.firstName} ${s.lastName || ''}`.trim() : (s.name || s.staff_name || 'Unknown')}
                </div>
                <Badge color={STATUS_COLOR_LIVE[st]} text={STATUS_LABEL[st] || st} />
                {s.clock_in_time && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>In: {fmt(s.clock_in_time)}</div>}
                {(s.minutes_worked_today != null || s.time_worked_formatted) && (
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Worked: {s.time_worked_formatted || `${Math.floor(s.minutes_worked_today / 60)}h ${s.minutes_worked_today % 60}m`}
                  </div>
                )}
                {(s.current_activity_category || s.latest_activity?.activity_category) && (
                  <Tooltip title={s.current_activity_description || s.latest_activity?.description}>
                    <Tag color="blue" style={{ marginTop: 4, fontSize: 11 }}>
                      {s.current_activity_category || s.latest_activity?.activity_category}
                    </Tag>
                  </Tooltip>
                )}
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  // ── Monthly report render ───────────────────────────────────
  const renderReport = () => {
    if (repLoading) return <Spin />;
    if (!repData) return <Empty description="No report data" />;
    const { data: rows, days_in_month } = repData;
    if (!rows?.length) return <Empty description="No data for selected month" />;
    const days = Array.from({ length: days_in_month }, (_, i) => i + 1);
    return (
      <div className="st-matrix-table" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', minWidth: 140 }}>Staff</th>
              {days.map(d => <th key={d}>{d}</th>)}
              <th>P</th><th>A</th><th>Hrs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.staff_id}>
                <td style={{ textAlign: 'left', fontWeight: 600 }}>{r.staff_name}</td>
                {days.map(d => {
                  const cell = r.days?.[d];
                  const st   = cell?.status || 'absent';
                  const css  = MATRIX_CSS[st] || 'A';
                  return <td key={d} className={`st-matrix-cell ${css}`}>{MATRIX_LABEL[st] || '-'}</td>;
                })}
                <td style={{ fontWeight: 600, color: '#389e0d' }}>{r.present_count}</td>
                <td style={{ fontWeight: 600, color: '#cf1322' }}>{r.absent_count}</td>
                <td style={{ fontWeight: 600 }}>{r.total_hours}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
          P = Present &nbsp; A = Absent &nbsp; H = Half Day &nbsp; L = Leave &nbsp; Ho = Holiday
        </div>
      </div>
    );
  };

  return (
    <div className="st-hub">
      <Tabs defaultActiveKey="live" onChange={onTabChange} type="card">

        {/* ── LIVE BOARD ── */}
        <TabPane tab="Live Board" key="live">
          <Card
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Real-time Staff Status</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Button icon={<ReloadOutlined />} onClick={() => loadLive()}>Refresh</Button>
            }
          >
            {renderLiveBoard()}
          </Card>
          {liveData?.summary && (
            <Card title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Summary</span>} headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }} style={{ marginTop: 16 }}>
              <Row gutter={[16, 16]}>
                {Object.entries(liveData.summary).map(([k, v]) => (
                  <Col xs={12} sm={6} key={k}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: STATUS_COLOR_LIVE[k] || '#000' }}>{v}</div>
                      <div style={{ fontSize: 12, color: '#8c8c8c', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          )}
        </TabPane>

        {/* ── DAILY ATTENDANCE ── */}
        <TabPane tab="Daily Attendance" key="daily">
          <Card
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Daily Attendance</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Space>
                <DatePicker
                  format="YYYY-MM-DD"
                  value={attDate ? moment(attDate, 'YYYY-MM-DD') : null}
                  onChange={(_, ds) => { setAttDate(ds); loadAttendance(ds); }}
                />
                <Button icon={<ReloadOutlined />} onClick={() => loadAttendance(attDate)} />
              </Space>
            }
          >
            <Table
              dataSource={attData}
              columns={attColumns}
              rowKey="staff_id"
              loading={attLoading}
              pagination={{ pageSize: 20 }}
              size="small"
            />
          </Card>
        </TabPane>

        {/* ── MONTHLY REPORT ── */}
        <TabPane tab="Monthly Report" key="report">
          <Card
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Monthly Attendance Matrix</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Space>
                <DatePicker.MonthPicker
                  value={moment(`${repYear}-${String(repMonth).padStart(2,'0')}`, 'YYYY-MM')}
                  onChange={m => {
                    if (!m) return;
                    const y = m.year(), mo = m.month() + 1;
                    setRepYear(y); setRepMonth(mo); loadReport(y, mo);
                  }}
                />
                <Button icon={<ReloadOutlined />} onClick={() => loadReport(repYear, repMonth)} />
              </Space>
            }
          >
            {renderReport()}
          </Card>
        </TabPane>

        {/* ── STAFF DRILL-DOWN ── */}
        <TabPane tab="Staff Drill-down" key="drilldown">
          <Card title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 14 }}>Staff Activity Drill-down</span>} headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
              <Select
                showSearch
                placeholder="Select staff"
                style={{ width: 220 }}
                onChange={sid => {
                  setDrillStaff(sid);
                  if (drillDate) loadDrill(sid, drillDate);
                }}
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {(liveData?.active_staff || []).map(s => (
                  <Option key={s.staff_id || s.id} value={s.staff_id || s.id}>
                    {s.firstName ? `${s.firstName} ${s.lastName || ''}`.trim() : (s.name || s.staff_name || 'Unknown')}
                  </Option>
                ))}
              </Select>
              <DatePicker
                format="YYYY-MM-DD"
                value={drillDate ? moment(drillDate, 'YYYY-MM-DD') : null}
                onChange={(_, ds) => {
                  setDrillDate(ds);
                  if (drillStaff) loadDrill(drillStaff, ds);
                }}
              />
              <Button
                type="primary"
                disabled={!drillStaff || !drillDate}
                onClick={() => loadDrill(drillStaff, drillDate)}
              >
                Load
              </Button>
            </Space>

            {drillLoading ? <Spin /> : (
              drillData.length > 0 ? (
                <Table
                  dataSource={drillData}
                  rowKey={(r, i) => i}
                  size="small"
                  pagination={false}
                  columns={[
                    { title: 'Time',     dataIndex: 'start_time', key: 't', render: fmt },
                    {
                      title: 'Category', key: 'cat',
                      render: (_, row) => row.type === 'break'
                        ? <Tag color="orange">{row.break_type || 'Break'}</Tag>
                        : <Tag color="blue">{row.activity_category || '-'}</Tag>,
                    },
                    {
                      title: 'Type', key: 'et',
                      render: (_, row) => row.type === 'break'
                        ? <Tag color="red">Break</Tag>
                        : <Tag color={row.entry_type === 'manual' ? 'orange' : 'green'}>{row.entry_type || 'auto'}</Tag>,
                    },
                    {
                      title: 'Duration', key: 'd',
                      render: (_, row) => {
                        const mins = row.duration_minutes || row.break_duration_minutes || 0;
                        return mins > 0 ? `${mins}m` : '-';
                      },
                    },
                    {
                      title: 'Description', key: 'desc', ellipsis: true,
                      render: (_, row) => row.description || row.notes || '-',
                    },
                  ]}
                />
              ) : <Empty description="Select staff and date, then click Load." />
            )}
          </Card>
        </TabPane>

      </Tabs>

      {/* Override Modal */}
      <Modal
        title={`Override Attendance — ${overrideRow?.staff_name}`}
        visible={overrideModal}
        onOk={saveOverride}
        onCancel={() => setOverrideModal(false)}
        okText="Save"
        confirmLoading={overSaving}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label>Status</label>
            <Select style={{ width: '100%', marginTop: 6 }} value={overStatus} onChange={setOverStatus}>
              <Option value="present">Present</Option>
              <Option value="absent">Absent</Option>
              <Option value="half_day">Half Day</Option>
              <Option value="leave">Leave</Option>
              <Option value="holiday">Holiday</Option>
            </Select>
          </div>
          {overStatus === 'leave' && (
            <div>
              <label>Leave Type</label>
              <Select style={{ width: '100%', marginTop: 6 }} value={overLeave} onChange={setOverLeave} allowClear>
                <Option value="sick">Sick</Option>
                <Option value="casual">Casual</Option>
                <Option value="wfh">Work from Home</Option>
                <Option value="other">Other</Option>
              </Select>
            </div>
          )}
          <div>
            <label>Notes</label>
            <TextArea rows={3} value={overNote} onChange={e => setOverNote(e.target.value)} style={{ marginTop: 6 }} />
          </div>
        </Space>
      </Modal>
    </div>
  );
}
